import React, { Component } from 'react';
import Inherit from '../../core/inherit';
import withNavigate from '../../core/withNavigate';
import { Layout, TableSkeleton, PageHeader, SectionCard, EmptyState } from '../common';
import { Button, Spin, Table, Tabs } from 'antd';
import { ArrowLeft, ChartColumn, ChartLine, Table2, Download, X, ShieldAlert, TriangleAlert, Layers, History } from 'lucide-react';
import dayjs from 'dayjs';
import { fmtCapture } from '../../utils/timeUtils';
import LevantamientoChartsController from './levantamientoCharts.controller';
import { DEFAULT_FILTERS, formatPriceName } from './levantamientoShared';
import { LevantamientoFilters, InfoNote, SourceTag, ProductThumb, CapturePrice, RoleTag } from './elements';
import PriceBySkuChart from './PriceBySkuChart';
import PriceTrendChart from './PriceTrendChart';
import PriceComparisonTables from './PriceComparisonTables';

/** Centered spinner that keeps the chart's height while loading. */
const ChartLoading = ({ height }) => (
    <div className="flex items-center justify-center" style={{ height }}>
        <Spin />
    </div>
);

/** Small legend chip for the SKU chart's color meaning. */
const LegendDot = ({ className, children }) => (
    <span className="inline-flex items-center gap-1.5 text-xs text-surface-500">
        <span className={`h-2 w-2 rounded-full ${className}`} />
        {children}
    </span>
);

class LevantamientoCharts extends Inherit(LevantamientoChartsController, Component) {
    state = {
        loading: true,
        chartLoading: false,
        accessDenied: false,
        error: false,
        chartRows: [],
        chartOverallAvg: 0,
        clientOptions: [],
        storeOptions: [],
        userOptions: [],
        categoryOptions: [],
        segmentOptions: [],
        tagOptions: [],
        gramsOptions: [],
        storeTypeOptions: [],
        dateRange: [dayjs().subtract(30, 'day'), dayjs()],
        filters: { ...DEFAULT_FILTERS },
        activeTab: 'sku',
        drillLoading: false,
        drillSku: null,
        drillRows: [],
        trendLoading: false,
        trendSeries: [],
        trendWeeks: [],
        trendTruncated: false,
        trendDrillProduct: null,
        trendDrillLoading: false,
        trendDrillPoints: [],
        comparisonLoading: false,
        comparison: null
    };

    drillRef = React.createRef();
    trendDrillRef = React.createRef();

    get drillColumns() {
        return [
            {
                title: 'Imagen',
                dataIndex: 'image',
                key: 'image',
                width: 64,
                align: 'center',
                render: (src) => <ProductThumb src={src} />
            },
            {
                title: 'Fuente',
                dataIndex: 'source',
                key: 'source',
                render: (s) => <SourceTag source={s} />
            },
            {
                title: 'Precio (USD)',
                dataIndex: 'priceUsd',
                key: 'priceUsd',
                align: 'right',
                sorter: (a, b) => (a.priceUsd ?? 0) - (b.priceUsd ?? 0),
                render: (p, r) => <CapturePrice value={p} isOutlier={r.isOutlier} title="Precio atípico — excluido del promedio" />
            },
            { title: 'Piso de venta', dataIndex: 'storeName', key: 'storeName' },
            {
                title: 'Tipo de tienda',
                dataIndex: 'storeType',
                key: 'storeType',
                render: (t) => t || <span className="text-surface-400">—</span>
            },
            { title: 'Cliente', dataIndex: 'clientName', key: 'clientName' },
            {
                title: 'Usuario',
                dataIndex: 'userName',
                key: 'userName',
                render: (u) => u || <span className="text-surface-400">—</span>
            },
            { title: 'Rol', dataIndex: 'userType', key: 'userType', render: (r) => <RoleTag role={r} /> },
            {
                title: 'Fecha',
                dataIndex: 'capturedAt',
                key: 'capturedAt',
                defaultSortOrder: 'descend',
                sorter: (a, b) => new Date(a.capturedAt) - new Date(b.capturedAt),
                render: (d, r) => <span className="whitespace-nowrap tabular-nums text-surface-600">{fmtCapture(d, r.source)}</span>
            }
        ];
    }

    /** Drill-down card header actions: export the rows and close the panel. */
    renderDrillActions(onExport, onClose) {
        return (
            <>
                <Button size="small" icon={<Download />} onClick={onExport}>
                    Exportar
                </Button>
                <Button size="small" type="text" icon={<X />} onClick={onClose}>
                    Cerrar
                </Button>
            </>
        );
    }

    // Tab 1: average price per SKU (ascending) + drill-down into its captures.
    renderSkuTab() {
        const { chartLoading, chartRows, chartOverallAvg, drillLoading, drillSku, drillRows } = this.state;
        return (
            <div className="space-y-4">
                <SectionCard
                    title="Precio promedio por SKU"
                    description="Precio promedio por producto en USD, de menor a mayor. Haz clic en un punto para ver los precios que componen su promedio."
                    icon={<ChartColumn />}
                    iconTone="brand"
                    extra={
                        <div className="hidden md:flex items-center gap-3">
                            <LegendDot className="bg-primary-600">Producto Mary</LegendDot>
                            <LegendDot className="bg-surface-400">Otros productos</LegendDot>
                        </div>
                    }>
                    {chartLoading ? (
                        <ChartLoading height={440} />
                    ) : (
                        <PriceBySkuChart rows={chartRows} overallAvg={chartOverallAvg} onPointClick={(point) => this.openDrill(point)} />
                    )}
                </SectionCard>

                {drillSku ? (
                    <div ref={this.drillRef}>
                        <SectionCard
                            flush
                            icon={<Layers />}
                            title={formatPriceName(drillSku)}
                            description={
                                <span className="tabular-nums">
                                    Promedio ${Number(drillSku.avgUsd).toFixed(2)} · {drillSku.count} precio(s)
                                </span>
                            }
                            extra={this.renderDrillActions(() => this.exportDrill(), () => this.closeDrill())}>
                            <Table
                                columns={this.drillColumns}
                                dataSource={drillRows}
                                loading={drillLoading}
                                rowKey={(r, i) => `${r.source}-${i}`}
                                size="middle"
                                scroll={{ x: 'max-content' }}
                                pagination={{ pageSize: 20, showSizeChanger: true, pageSizeOptions: ['20', '50', '100'] }}
                            />
                        </SectionCard>
                    </div>
                ) : null}
            </div>
        );
    }

    get trendDrillColumns() {
        return [
            {
                title: 'Semana',
                dataIndex: 'week',
                key: 'week',
                defaultSortOrder: 'descend',
                sorter: (a, b) => a.week.localeCompare(b.week),
                render: (w) => <span className="tabular-nums">{dayjs(w).format('DD/MM/YYYY')}</span>
            },
            {
                title: 'Precio promedio (USD)',
                dataIndex: 'avgUsd',
                key: 'avgUsd',
                align: 'right',
                sorter: (a, b) => a.avgUsd - b.avgUsd,
                render: (p) => <span className="font-medium tabular-nums text-surface-900">{`$${Number(p).toFixed(2)}`}</span>
            },
            {
                title: '# precios',
                dataIndex: 'count',
                key: 'count',
                align: 'right',
                sorter: (a, b) => a.count - b.count,
                render: (c) => <span className="tabular-nums">{c}</span>
            }
        ];
    }

    // Tab 2: weekly average price per product over time; click a line for its full history.
    renderTendenciasTab() {
        const { trendLoading, trendSeries, trendWeeks, trendTruncated, trendDrillProduct, trendDrillLoading, trendDrillPoints } =
            this.state;
        return (
            <div className="space-y-4">
                <SectionCard
                    title="Tendencia semanal"
                    description={
                        trendSeries.length ? (
                            <>
                                Precio promedio semanal por producto (USD). Pasa el cursor sobre una línea para resaltarla, haz
                                clic en ella para ver su historial completo, o usa la leyenda para ocultar productos.
                                {trendTruncated ? ` Mostrando los ${trendSeries.length} productos con más datos.` : ''}
                            </>
                        ) : null
                    }
                    icon={<ChartLine />}
                    iconTone="brand">
                    {trendLoading ? (
                        <ChartLoading height={640} />
                    ) : trendSeries.length ? (
                        <PriceTrendChart series={trendSeries} weeks={trendWeeks} onLineClick={(p) => this.openTrendDrill(p)} />
                    ) : (
                        <EmptyState
                            icon={<ChartLine />}
                            title="Sin datos para los filtros actuales"
                            description="Amplía el rango de fechas o quita algún filtro."
                        />
                    )}
                </SectionCard>

                {trendDrillProduct ? (
                    <div ref={this.trendDrillRef}>
                        <SectionCard
                            flush
                            icon={<History />}
                            title={formatPriceName(trendDrillProduct)}
                            description="Historial semanal completo"
                            extra={this.renderDrillActions(() => this.exportTrendDrill(), () => this.closeTrendDrill())}>
                            <Table
                                columns={this.trendDrillColumns}
                                dataSource={trendDrillPoints}
                                loading={trendDrillLoading}
                                rowKey={(r) => r.week}
                                size="middle"
                                scroll={{ x: 'max-content' }}
                                pagination={{ pageSize: 20, showSizeChanger: true, pageSizeOptions: ['20', '50', '100'] }}
                            />
                        </SectionCard>
                    </div>
                ) : null}
            </div>
        );
    }

    // Tab 3: Mary vs competition average-price comparison tables.
    renderComparacionesTab() {
        const { comparisonLoading, comparison, filters } = this.state;
        if (!filters.category?.length) {
            return (
                <div className="shelf-card">
                    <EmptyState
                        icon={<Layers />}
                        title="Selecciona una categoría"
                        description="La comparación se calcula por categoría. Elige una categoría en los filtros para ver el análisis."
                    />
                </div>
            );
        }
        return (
            <SectionCard
                title="Index Mary vs. competencia"
                description="Precio promedio de los productos Mary frente a la competencia dentro de los filtros seleccionados. Filtra por categoría/segmento para una comparación relevante."
                icon={<Table2 />}
                iconTone="brand"
                extra={
                    <Button
                        size="small"
                        icon={<Download />}
                        disabled={comparisonLoading || !comparison?.mary?.length}
                        onClick={() => this.exportComparison()}>
                        Exportar
                    </Button>
                }>
                {comparisonLoading ? (
                    <ChartLoading height={400} />
                ) : (
                    <>
                        <InfoNote className="mb-5">
                            Las comparaciones de la primera tabla (Prom. Comp.) se basan en el gramaje: cada producto Mary se
                            compara únicamente con la competencia del mismo gramaje.
                        </InfoNote>
                        <PriceComparisonTables
                            mary={comparison?.mary || []}
                            competition={comparison?.competition || []}
                            compByGrams={comparison?.compByGrams || {}}
                            maryOverallAvg={comparison?.maryOverallAvg || 0}
                            compOverallAvg={comparison?.compOverallAvg || 0}
                        />
                    </>
                )}
            </SectionCard>
        );
    }

    render() {
        const {
            loading,
            accessDenied,
            error,
            clientOptions,
            storeOptions,
            userOptions,
            categoryOptions,
            segmentOptions,
            tagOptions,
            gramsOptions,
            storeTypeOptions,
            dateRange,
            filters,
            activeTab
        } = this.state;

        return (
            <Layout current="levantamiento-general">
                {this.props.contextHolder}

                <Button type="text" size="small" icon={<ArrowLeft />} className="mb-3 -ml-2 text-surface-500" onClick={() => this.goBack()}>
                    Volver al levantamiento
                </Button>
                <PageHeader
                    icon={<ChartColumn />}
                    title="Análisis de precio"
                    description="Análisis del levantamiento general. Ajusta los filtros para acotar los datos."
                />

                {accessDenied ? (
                    <div className="shelf-card">
                        <EmptyState
                            tone="danger"
                            icon={<ShieldAlert />}
                            title="Acceso restringido"
                            description="Esta vista está disponible para administradores y gerentes."
                        />
                    </div>
                ) : error ? (
                    <div className="shelf-card">
                        <EmptyState tone="danger" icon={<TriangleAlert />} title="Error al cargar el gráfico de precios" />
                    </div>
                ) : loading ? (
                    <TableSkeleton rows={8} columns={6} />
                ) : (
                    <>
                        <LevantamientoFilters
                            filters={filters}
                            dateRange={dateRange}
                            clientOptions={clientOptions}
                            storeOptions={storeOptions}
                            userOptions={userOptions}
                            categoryOptions={categoryOptions}
                            segmentOptions={segmentOptions}
                            tagOptions={tagOptions}
                            gramsOptions={gramsOptions}
                            storeTypeOptions={storeTypeOptions}
                            showSearch={false}
                            onFilter={(key, value) => this.setFilter(key, value)}
                            onDateRange={(range) => this.setDateRange(range)}
                        />

                        <Tabs
                            activeKey={activeTab}
                            onChange={(key) => this.onTabChange(key)}
                            items={[
                                {
                                    key: 'sku',
                                    label: (
                                        <span className="inline-flex items-center gap-1.5">
                                            <ChartColumn /> Precios por SKU
                                        </span>
                                    ),
                                    children: this.renderSkuTab()
                                },
                                {
                                    key: 'tendencias',
                                    label: (
                                        <span className="inline-flex items-center gap-1.5">
                                            <ChartLine /> Tendencias
                                        </span>
                                    ),
                                    children: this.renderTendenciasTab()
                                },
                                {
                                    key: 'comparaciones',
                                    label: (
                                        <span className="inline-flex items-center gap-1.5">
                                            <Table2 /> Index
                                        </span>
                                    ),
                                    children: this.renderComparacionesTab()
                                }
                            ]}
                        />
                    </>
                )}
            </Layout>
        );
    }
}

export default withNavigate(LevantamientoCharts);
